// src/products/equityManager/pages/TransactionsPage.js
import React, { useState, useEffect } from 'react';
import { Plus, ArrowRightLeft, X, AlertCircle, CheckCircle } from 'lucide-react';
import EquityService from '../../../services/equityService';
import CurrencyService from '../../../services/currencyService';
import formatDate from '../utils/dateFormatter';
import CreateTransactionModal from '../components/CreateTransactionModal';

const TransactionsPage = ({ selectedCompany, addError, addSuccess }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    const loadTransactions = async () => {
      try {
        setLoading(true);
        const data = await EquityService.fetchShareTransactions(selectedCompany.id);
        setTransactions(data);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        console.error('Transaction error details:', JSON.stringify(error, null, 2));
        addError('Failed to fetch share transactions');
      } finally {
        setLoading(false);
      }
    };

    if (selectedCompany?.id) {
      loadTransactions();
    }
  }, [selectedCompany?.id, addError]);

  const handleCreateTransaction = async (transactionData) => {
    try {
      await EquityService.createShareTransaction({
        ...transactionData,
        company_id: selectedCompany.id
      });
      
      addSuccess('Transaction created successfully');
      setShowCreateModal(false);
      
      // Small delay to ensure database has processed the transaction
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refetch transactions
      try {
        setLoading(true);
        const data = await EquityService.fetchShareTransactions(selectedCompany.id);
        setTransactions(data);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        addError('Failed to fetch share transactions');
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      addError(`Failed to create transaction: ${error.message || 'Unknown error'}`);
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'transfer':
        return 'bg-blue-100 text-blue-800';
      case 'void':
        return 'bg-red-100 text-red-800';
      case 'buyback':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'transfer':
        return <ArrowRightLeft className="h-4 w-4" />;
      case 'void':
        return <X className="h-4 w-4" />;
      case 'buyback':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const formatTransactionParties = (transaction) => {
    const { transaction_type, from_shareholder_name, to_shareholder_name } = transaction;
    
    switch (transaction_type) {
      case 'transfer':
        return `${from_shareholder_name} → ${to_shareholder_name}`;
      case 'void':
        return from_shareholder_name;
      case 'buyback':
        return `${from_shareholder_name} → Company`;
      default:
        return from_shareholder_name || 'Unknown';
    }
  };

  if (!selectedCompany) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Selected</h3>
        <p className="text-gray-600">Please select a company to view share transactions.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Share Transactions</h1>
          <p className="text-gray-600 mt-1">
            Manage transfers, voids, and buybacks for {selectedCompany.name}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Transaction
        </button>
      </div>

      {/* Transaction Types Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">Transaction Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center">
            <ArrowRightLeft className="h-4 w-4 text-blue-600 mr-2" />
            <div>
              <span className="font-medium text-blue-900">Transfer:</span>
              <span className="text-blue-700 ml-1">Move shares between shareholders</span>
            </div>
          </div>
          <div className="flex items-center">
            <X className="h-4 w-4 text-red-600 mr-2" />
            <div>
              <span className="font-medium text-red-900">Void:</span>
              <span className="text-red-700 ml-1">Cancel erroneous issuances</span>
            </div>
          </div>
          <div className="flex items-center">
            <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
            <div>
              <span className="font-medium text-green-900">Buyback:</span>
              <span className="text-green-700 ml-1">Company repurchases shares</span>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-12 text-center">
            <ArrowRightLeft className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Transactions Yet</h3>
            <p className="text-gray-600 mb-4">
              Create your first share transaction to track transfers, voids, or buybacks.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Create First Transaction
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shares & Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatTransactionParties(transaction)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTransactionTypeColor(transaction.transaction_type)}`}>
                        {getTransactionTypeIcon(transaction.transaction_type)}
                        <span className="ml-1 capitalize">{transaction.transaction_type}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {transaction.shares.toLocaleString()} shares
                      </div>
                      <div className="text-sm text-gray-500">
                        {transaction.share_class_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {transaction.price_per_share 
                          ? CurrencyService.formatAmount(parseFloat(transaction.price_per_share), selectedCompany?.currency || 'USD')
                          : 'N/A'}
                      </div>
                      {transaction.total_amount > 0 && (
                        <div className="text-sm text-gray-500">
                          Total: {CurrencyService.formatAmount(transaction.total_amount, selectedCompany?.currency || 'USD')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.transaction_date)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {transaction.notes || '-'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Transaction Modal */}
      {showCreateModal && (
        <CreateTransactionModal
          selectedCompany={selectedCompany}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTransaction}
          addError={addError}
        />
      )}
    </div>
  );
};

export default TransactionsPage;